import { getOneAccount } from "api/accounts";
import { JsonResp, unwrapJsonResp } from "api/JsonResp";
import {
  getUserFriendlyErrorMessage,
  UserFriendlyError,
} from "api/UserFriendlyError";
import axios from "axios";
import { AccountType } from "shared/types/accounts";
import { FacebookScopes } from "../facebook";
import {
  FacebookAuthResponse,
  fbLogin,
  FBPageWithInsta,
  fbReadInstaBizAccount,
  fbReadMe,
  fbReadPages,
  getFacebookAuthType,
} from "../facebook/fb-sdk-utils";
import { toast } from "react-toastify";
import { selectCurrentUser } from "../../../entities/current-user";
import { resolveReduxAppStore } from "../../../entities/lib/redux-app-store";

export type {
  FBPageWithInsta,
  InstaAccount,
  FBPage,
} from "../facebook/fb-sdk-utils";

export * from "./posting";

const InstagramScopesBasic = [
  "instagram_basic",
  "instagram_manage_insights",
  "business_management",
];

export const InstagramScopeInbox = [
  "instagram_manage_messages",
  "pages_messaging",
];
export const RequiredInstagramScopes = [
  ...InstagramScopesBasic,
  ...InstagramScopeInbox,
];

export const InstagramScopeInboxComments = ["instagram_manage_comments"];

export async function getInstagramAccounts(
  {
    additionalScopes,
    useShortPagesFbApi,
  }: {
    additionalScopes: string[];
    useShortPagesFbApi: boolean;
  } = { additionalScopes: [], useShortPagesFbApi: false }
) {
  let scopes = [
    ...RequiredInstagramScopes,
    ...additionalScopes,
    ...FacebookScopes,
  ];
  try {
    const appStore = resolveReduxAppStore();
    const user = appStore
      ? selectCurrentUser(appStore.getState() as never)
      : undefined;

    useShortPagesFbApi =
      user?.preferences?.useShortPagesFbApi || useShortPagesFbApi || false;
    console.log("Doing FB Login");
    const auth = await fbLogin(scopes);
    console.log("FB login complete, reading me");
    await fbReadMe(auth);
    console.log("Completed read me, reading pages for IG");
    if (useShortPagesFbApi)
      console.log(
        "Using shorter pagination limit due to useShortPagesFbApi boolean being true."
      );
    const fb_accounts = await fbReadPages(auth, useShortPagesFbApi);
    console.log("FB pages for IG read", fb_accounts);
    // check if all FB accounts
    // TODO: ensure we really need this call
    if (fb_accounts.length === 0) {
      console.log(
        "Facebook did not return any Instagram accounts for this user.",
        fb_accounts
      );
      throw new UserFriendlyError(
        "Facebook did not return any Instagram accounts when you connected. Are you logged into the right Facebook account?"
      );
    }
    const pages = await Promise.all(
      // used to be acc,auth,fb_accounts??? but fb_accounts isn't used in the function
      fb_accounts.map((acc) => fbReadInstaBizAccount(acc, auth))
    );
    return {
      session: auth,
      pages,
    };
  } catch (e) {
    console.error(e);
    toast.error(getUserFriendlyErrorMessage(e as Error));
    throw e;
  }
}

export async function addInstagramAccount({
  page,
  session,
}: {
  page: FBPageWithInsta;
  session: FacebookAuthResponse;
}): Promise<AccountType> {
  const response = await axios.request<AccountType>({
    method: "post",
    url: "/accounts/instagram",
    data: {
      session,
      facebookAuthType: getFacebookAuthType(),
      userToken: session.accessToken,
      user_token: session.accessToken,
      ...page.page,
      igData: page.ig,
    },
  });
  return response.data;
}

export async function addInstagramAPIkey(
  accountId: string,
  {
    session,
    page,
  }: { session: FacebookAuthResponse; page: Required<FBPageWithInsta> }
): Promise<AccountType> {
  const response = await axios.request<JsonResp<string>>({
    method: "put",
    url: `/accounts/addIgToken/${accountId}`,
    data: {
      igData: {
        ...page.ig,
        access_token: page.page.access_token,
        facebookAuthType: getFacebookAuthType(),
        user_token: session.accessToken,
        userToken: session.accessToken,
        associatedPageId: page.page.id,
      },
    },
  });

  unwrapJsonResp(response.data);

  return await getOneAccount(accountId);
}
